import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchVoidReasons,
  createVoidReason,
  updateVoidReason,
  deleteVoidReason,
} from '../../api/voidReasons';

export default function VoidReasonsManager({ authConfig, onUnauthorized }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchVoidReasons(authConfig);
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.message || 'Failed to load void reasons.');
    } finally {
      setLoading(false);
    }
  }, [authConfig, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const startAdd = () => {
    setEditingId(null);
    setShowAddModal(false);
    setCode('');
    setLabel('');
  };

  const openAdd = () => {
    startAdd();
    setShowAddModal(true);
  };

  const startEdit = (item) => {
    setEditingId(item._id || item.id);
    setShowAddModal(false);
    setCode(item.code || '');
    setLabel(item.label || item.name || '');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!code.trim() || !label.trim()) {
      setError('Code and label are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { code: code.trim(), label: label.trim() };
      if (editingId) {
        await updateVoidReason(editingId, payload, authConfig);
      } else {
        await createVoidReason(payload, authConfig);
      }
      startAdd();
      await load();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this void reason?')) return;
    try {
      await deleteVoidReason(id, authConfig);
      await load();
      if (editingId === id) startAdd();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.message || 'Failed to delete.');
    }
  };

  const renderForm = ({ submitLabel }) => (
    <form onSubmit={handleSave} className="space-y-3 p-5">
      <input
        type="text"
        placeholder="Code (e.g. CUST)"
        className="border p-2 rounded text-sm w-full"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
      />
      <input
        type="text"
        placeholder="Label (e.g. Customer changed mind)"
        className="border p-2 rounded text-sm w-full"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        required
      />
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={saving} className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded text-sm disabled:opacity-60 hover:bg-indigo-500">
          {saving ? 'Saving...' : submitLabel}
        </button>
        <button type="button" onClick={startAdd} className="flex-1 px-4 py-2 border rounded text-sm hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </form>
  );

  const renderActions = (item) => (
    <div className="flex gap-2">
      <button type="button" onClick={() => startEdit(item)} className="flex-1 text-sm border px-3 py-2 rounded hover:bg-gray-50">Edit</button>
      <button type="button" onClick={() => handleDelete(item._id || item.id)} className="flex-1 text-sm bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700">Delete</button>
    </div>
  );

  if (loading) return <p className="text-gray-600">Loading void reasons...</p>;

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-medium">Void reasons</h4>
          <p className="text-xs text-gray-500">Manage POS order void reason choices.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={openAdd} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"><span className="text-lg leading-none">+</span>Add</button>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
            <button type="button" onClick={() => setViewMode('list')} className={`rounded-md px-3 py-1.5 ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>List</button>
            <button type="button" onClick={() => setViewMode('grid')} className={`rounded-md px-3 py-1.5 ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Grid</button>
          </div>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="rounded border p-3 text-sm text-gray-500">No void reasons yet.</div>
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {list.map((item) => (
            <div key={item._id || item.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="truncate font-semibold text-gray-900">{item.code || item.label || item.name}</p>
              <p className="mb-4 truncate text-sm text-gray-500">{item.label || item.name || 'No label'}</p>
              {renderActions(item)}
            </div>
          ))}
        </div>
      ) : (
        <ul className="border rounded divide-y">
          {list.map((item) => (
            <li key={item._id || item.id} className="p-3 flex items-center justify-between gap-3">
              <span className="text-sm">
                {item.code || item.label || item.name}
                {(item.code && item.label) ? ` - ${item.label}` : ''}
              </span>
              <div className="w-44 shrink-0">{renderActions(item)}</div>
            </li>
          ))}
        </ul>
      )}

      {showAddModal && !editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="add-void-reason-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="add-void-reason-title" className="font-semibold text-gray-900">Add void reason</h4>
              <button type="button" onClick={startAdd} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close add void reason">x</button>
            </div>
            {renderForm({ submitLabel: 'Add void reason' })}
          </div>
        </div>
      )}

      {editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="edit-void-reason-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="edit-void-reason-title" className="font-semibold text-gray-900">Edit void reason</h4>
              <button type="button" onClick={startAdd} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close edit void reason">x</button>
            </div>
            {renderForm({ submitLabel: 'Update void reason' })}
          </div>
        </div>
      )}
    </div>
  );
}
